import { useQuery } from "@tanstack/react-query";

import { AuditTab } from "./AuditTab";
import { FactsTab } from "./FactsTab";
import { InboxTab } from "./InboxTab";
import { listEveryFact, type FreshnessMeta } from "./api";

/**
 * The head query used both to source the freshness line and, implicitly, to
 * warm the query cache the tabs also read from. Correction #2 of the
 * canonical spec: this module renders TAB CONTENT ONLY — `ShellPage` already
 * wraps every module in the shell-owned `ModuleViewport`, so this file must
 * not render one (a double frame `check-shell-ownership.mjs` cannot catch,
 * because it only flags a *definition*, not a double-wrap).
 */
export function useProtexMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({
    queryKey: ["protex", "facts", "all"],
    queryFn: () => listEveryFact({}, { limit: PAGE_HEAD_SIZE }),
    enabled,
  });
  return enabled ? head.data?.meta : undefined;
}

// Matches FactsTab's own unfiltered read — same key, same reader, same page
// size — so the head query and the tab share ONE cache entry instead of firing
// two sets of requests for the same rows.
//
// The reader has to match as well as the key. React Query keys the cache, but
// the `queryFn` that wins is whichever observer triggers the fetch: a head
// query still calling `listFacts` here would hand the tab a capped 200-record
// page under the key it reads completeness off, and the tab would never refetch
// it. That is the bug this shared read exists to close, re-introduced by the
// back door.
const PAGE_HEAD_SIZE = 200;

/**
 * `decision` and `ownerName` are part of the shell's native-module props but
 * are deliberately NOT read here: permission rendering is shell-owned (I-7,
 * AGENTS boundary 3), and `ShellPage` has already applied `PermissionGate`
 * around everything this returns. A module that branches on `decision` is a
 * second permission renderer, and two renderers drift.
 */
export function ProtexModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  if (activeTabKey === "audit") return <AuditTab areaName={areaName} />;
  // Every Protex viewer sees the tab (manifest `min_level: view`, GATE-476). The decide
  // form sits in the shell's `WriteOnly`, which does not render it for a view decision;
  // the API refuses `/decide` below `edit` as well. This module checks neither.
  if (activeTabKey === "inbox") return <InboxTab areaName={areaName} />;
  return <FactsTab areaName={areaName} />;
}
